"use client";

import { Button, Input, Label } from "@acme/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import circlesLogo from "@public/static/circles-logo.png";
import { ArrowLeft, Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { requestPasswordReset } from "~/lib/auth/better-auth-client";
import AuthRightPanel from "../../right-pannel";
import TermsAndServicesAgreement from "../../terms-and-services";

// Define validation schema
const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordComponent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const router = useRouter();

  const t = useTranslations("auth.passwordForget");

  // Set up form validation with react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onBlur",
  });

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 px-2 py-4 sm:p-4 dark:from-gray-900 dark:to-gray-950">
      <div className="mx-auto my-2 flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-auto rounded-xl bg-white shadow-xl lg:flex-row dark:bg-gray-800">
        <div className="flex w-full flex-col items-center overflow-auto p-4 sm:p-6 md:p-12 lg:w-1/2 lg:items-start">
          <div className="mb-4 flex items-center sm:mb-6">
            <Image width={40} height={40} src={circlesLogo} alt="Logo" />
            <span className="text-lg font-semibold sm:text-xl">Optogrid</span>
          </div>

          <div className="mb-3 sm:mb-6">
            <h1 className="mb-1 text-xl font-bold tracking-tight sm:mb-2 sm:text-2xl">
              {t("title")}
            </h1>
            <p className="text-xs text-gray-500 sm:text-sm dark:text-gray-400">
              {t("descriptionForm")}
            </p>
          </div>

          {success ? (
            <div className="grid w-full max-w-md gap-3 sm:gap-4">
              <div className="rounded-md bg-green-50 p-4 dark:bg-green-900/20">
                <p className="text-sm text-green-600 dark:text-green-400">
                  {t("linkSentMessage")}
                </p>
              </div>
              <Button className="mt-4" onClick={() => router.push("/access")}>
                {t("returnToSignInBtn")}
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(async (data) => {
                setLoading(true);
                setError(null);

                try {
                  const result = await requestPasswordReset({
                    email: data.email,
                    redirectTo: "/access/email-forget-callback",
                  });

                  if (result.error) {
                    setError(
                      result.error.message ||
                        "Failed to send password reset email",
                    );
                  } else {
                    setSuccess(true);
                  }
                } catch (error) {
                  console.error("Password reset error:", error);
                  setError("An error occurred. Please try again.");
                } finally {
                  setLoading(false);
                }
              })}
              className="grid w-full max-w-md gap-3 sm:gap-4"
            >
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  autoComplete="email"
                  {...register("email")}
                  aria-invalid={errors.email ? "true" : "false"}
                />
                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email.message}</p>
                )}
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {error}
                  </p>
                </div>
              )}

              <Button type="submit" className="mt-4 w-full" disabled={loading}>
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  "Send reset link"
                )}
              </Button>

              <div className="mt-2 text-center text-sm text-gray-600 sm:mt-4 dark:text-gray-400">
                <Link
                  href="/access"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  <ArrowLeft size={16} />
                  {t("backToSignInLink")}
                </Link>
              </div>
            </form>
          )}

          <TermsAndServicesAgreement />
        </div>

        {/* Right panel with image - only visible on desktop */}
        {/* <div className="to-primary-dark hidden bg-gradient-to-br from-primary lg:block lg:w-1/2">
          <div className="relative h-full w-full p-8">
            <div
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: "url('/static/circles-logo.svg')" }}
            ></div>
            <div className="relative z-10 flex h-full flex-col items-center justify-center text-white">
              <h2 className="mb-4 text-2xl font-bold sm:text-3xl">
                Measure PD online
              </h2>
              <p className="mb-4 max-w-md text-center text-base sm:mb-8 sm:text-lg">
                Simple PD measurement tools for eyewear manufacturers. Measure
                your PD for free.
              </p>
              <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm sm:p-6">
                <blockquote className="italic">
                  "Optogrid has transformed the way our customers sell eyewear
                  online."
                </blockquote>
              </div>
            </div>
          </div>
        </div> */}
        <AuthRightPanel />
      </div>
    </div>
  );
}
